import type { ReportFamily, ReportSummary } from '@/shared/api/reports';
import { REPORT_FAMILIES } from '@/shared/api/reports';

/**
 * `validateSearch` of `/reports` and `/reports/$reportKey` (13 §4.11), kept apart from the pages
 * so neither page file exports anything but a component.
 */

export interface ReportsCatalogSearch {
  family?: ReportFamily;
}

/** `?family=` narrows the catalogue to one section; anything else is dropped, never thrown on. */
export function reportsCatalogSearchSchema(search: Record<string, unknown>): ReportsCatalogSearch {
  const family = search['family'];
  return typeof family === 'string' && REPORT_FAMILIES.includes(family as ReportFamily)
    ? { family: family as ReportFamily }
    : {};
}

/** Reserved keys of the query string: everything else is one of the report's own filters. */
const RESERVED_KEYS = new Set(['from', 'to', 'groupBy']);

/**
 * `from`, `to`, `groupBy` (comma-separated) and the report's own filters, every one an optional
 * string (AM-130 point 4) - a report's filters vary by key, so this stays a flat bag rather than a
 * fixed shape, and `reportFiltersOf` below picks out the ones a given report declares.
 */
export type ReportDetailSearch = Record<string, string>;

export function reportDetailSearchSchema(search: Record<string, unknown>): ReportDetailSearch {
  const result: ReportDetailSearch = {};
  for (const [key, value] of Object.entries(search)) {
    if (typeof value === 'string' && value.length > 0) {
      result[key] = value;
    } else if (typeof value === 'boolean') {
      // `AM-876`: `billed=true` reaches the page as a boolean (the router reads each value as JSON).
      result[key] = String(value);
    }
  }
  return result;
}

/**
 * `AM-876`: the search middleware of `/reports/$reportKey`. The router writes a string that reads as JSON with quotes,
 * so "Facturado" (`billed`, `true`) left `billed=%22true%22` in the address; `true` and `false` go as they are, and
 * {@link reportDetailSearchSchema} turns them back into the strings the page reads.
 */
export function reportDetailUrlSearch({
  search,
  next,
}: {
  search: ReportDetailSearch;
  next: (search: ReportDetailSearch) => ReportDetailSearch;
}): ReportDetailSearch {
  const written: Record<string, string | boolean> = {};
  for (const [key, value] of Object.entries(next(search))) {
    written[key] = value === 'true' ? true : value === 'false' ? false : value;
  }
  // The address only: what the page reads always goes through `reportDetailSearchSchema`.
  return written as ReportDetailSearch;
}

/** The dimensions of `search.groupBy`, in the order the URL carries them (at most two, AM-130 point 4). */
export function groupByOf(search: ReportDetailSearch): string[] {
  const raw = search['groupBy'];
  return raw === undefined ? [] : raw.split(',').filter((dimension) => dimension.length > 0);
}

/**
 * Every search entry but `from`/`to`/`groupBy`, whatever the key - the first paint's guess at the
 * filters before the catalogue names which of them this report actually declares (`reportFiltersOf`
 * is the one the query itself is built from, once the catalogue answers).
 */
export function nonReservedSearchEntries(search: ReportDetailSearch): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (!RESERVED_KEYS.has(key)) {
      result[key] = value;
    }
  }
  return result;
}

/** The filters a report declares, read out of the flat search bag; an unlisted key is never sent. */
export function reportFiltersOf(
  search: ReportDetailSearch,
  definition: ReportSummary,
): Record<string, string> {
  const filters: Record<string, string> = {};
  for (const parameter of definition.parameters) {
    const value = search[parameter.name];
    if (value !== undefined && !RESERVED_KEYS.has(parameter.name)) {
      filters[parameter.name] = value;
    }
  }
  return filters;
}
